import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import { Timestamp } from 'firebase/firestore';
import { productionLogService } from '../services/production-log.service';
import { useAuth } from '../context/AuthContext';
import { ProductionLog, CavityInfo } from '../types';

interface ProductionLogModalProps {
  open: boolean;
  onClose: () => void;
  moldId: string;
  moldNumber: string;
  currentShots: number;
  cavities?: CavityInfo[];
  onSuccess?: () => void;
}

export const ProductionLogModal: React.FC<ProductionLogModalProps> = ({
  open,
  onClose,
  moldId,
  moldNumber,
  currentShots,
  cavities,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [date, setDate] = useState<Date | null>(new Date());
  const [shots, setShots] = useState<string>('');
  const [workOrderNumber, setWorkOrderNumber] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [selectedCavities, setSelectedCavities] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 모달이 열릴 때 초기화
  useEffect(() => {
    if (open) {
      setDate(new Date());
      setShots('');
      setWorkOrderNumber('');
      setNotes('');
      setSelectedCavities([]);
      setError(null);
    }
  }, [open]);

  // 패밀리 선택 핸들러
  const handleCavityToggle = (cavityNumber: number) => {
    setSelectedCavities(prev => {
      if (prev.includes(cavityNumber)) {
        return prev.filter(num => num !== cavityNumber);
      } else {
        return [...prev, cavityNumber];
      }
    });
  };

  const handleSave = async () => {
    // 유효성 검사
    if (!date) {
      setError('날짜를 선택해주세요.');
      return;
    }

    const shotsNum = parseInt(shots);
    if (!shots || isNaN(shotsNum) || shotsNum <= 0) {
      setError('유효한 생산량을 입력해주세요.');
      return;
    }

    // 패밀리 선택 검증
    if (cavities && cavities.length > 0 && selectedCavities.length === 0) {
      setError('최소 1개 이상의 패밀리를 선택해주세요.');
      return;
    }

    if (!user) {
      setError('로그인 정보를 확인할 수 없습니다.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // 날짜의 시작 시간으로 설정 (00:00:00)
      const logDate = new Date(date);
      logDate.setHours(0, 0, 0, 0);

      const logData: Omit<ProductionLog, 'id' | 'createdAt'> = {
        moldId,
        moldNumber,
        date: Timestamp.fromDate(logDate),
        shots: shotsNum,
        startShots: currentShots,
        endShots: currentShots + shotsNum,
        source: 'manual',
        operatorId: user.uid,
        operatorName: user.displayName || user.email || '알 수 없음',
        updatedAt: Timestamp.now(),
        ...(workOrderNumber && { workOrderNumber }),
        ...(notes && { notes }),
        ...(cavities && cavities.length > 0 && selectedCavities.length > 0 && { selectedCavities }),
      };

      await productionLogService.create(logData);

      // 성공 콜백
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (err) {
      console.error('생산이력 저장 실패:', err);
      setError('생산이력 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>일일 생산량 입력</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {/* 금형 정보 */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'primary.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'primary.200',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              금형번호
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              {moldNumber}
            </Typography>
          </Box>

          {/* 날짜 선택 */}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <DatePicker
              label="생산일자 *"
              value={date}
              onChange={(newDate) => setDate(newDate)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: '생산 작업이 이루어진 날짜를 선택하세요',
                },
              }}
            />
          </LocalizationProvider>

          {/* 생산량 입력 */}
          <TextField
            label="생산량 (쇼트수) *"
            type="number"
            value={shots}
            onChange={(e) => setShots(e.target.value)}
            fullWidth
            helperText="당일 생산한 총 쇼트수를 입력하세요"
            inputProps={{ min: 1 }}
          />

          {/* 작업자 정보 (읽기 전용) */}
          <TextField
            label="작업자"
            value={user?.displayName || user?.email || ''}
            fullWidth
            disabled
            helperText="현재 로그인한 사용자"
          />

          {/* 패밀리 선택 */}
          {cavities && cavities.length > 0 && (
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                사용 패밀리 선택 *
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {cavities.map((cavity) => (
                  <FormControlLabel
                    key={cavity.cavityNumber}
                    control={
                      <Checkbox
                        checked={selectedCavities.includes(cavity.cavityNumber)}
                        onChange={() => handleCavityToggle(cavity.cavityNumber)}
                      />
                    }
                    label={`패밀리 ${cavity.cavityNumber}: ${cavity.productName}`}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                생산에 사용된 패밀리를 선택하세요 (복수 선택 가능)
              </Typography>
            </Box>
          )}

          {/* 작업지시번호 (선택) */}
          <TextField
            label="작업지시번호"
            value={workOrderNumber}
            onChange={(e) => setWorkOrderNumber(e.target.value)}
            fullWidth
            helperText="(선택) 작업지시번호가 있다면 입력하세요"
          />

          {/* 비고 (선택) */}
          <TextField
            label="비고"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
            helperText="(선택) 특이사항이나 메모를 입력하세요"
          />

          {/* 에러 메시지 */}
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ color: 'white' }}>
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
